import { Suspense, lazy, useEffect, useState } from "react";
import { ZoomIn } from "lucide-react";

const ZoomViewer = lazy(() => import("@/components/site/ZoomViewer"));

interface ZoomableImageProps {
  src: string;
  alt: string;
  children: React.ReactNode;
}

export function ZoomableImage({ src, alt, children }: ZoomableImageProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open full-size view of ${alt}`}
        className="group relative block w-full cursor-zoom-in text-left"
      >
        {children}
        <span className="pointer-events-none absolute right-4 bottom-4 rounded-full bg-ink/70 p-2 text-ink-foreground opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
          <ZoomIn className="size-5" aria-hidden="true" />
        </span>
      </button>

      {mounted && open && (
        <Suspense fallback={null}>
          <ZoomViewer src={src} alt={alt} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
